import { z } from 'zod';
import { CiphertextBox, ContentBase64Url } from './content.js';
import { ApprovalBatch, WebAuthnAssertion } from './signing.js';
const Id = z.string().min(1).max(256);
const Counter = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const Digest = ContentBase64Url.refine((s) => s.length === 43);
export const ContentPublicKey = z.strictObject({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: Digest,
    y: Digest,
});
export const ContentKeyWrap = z.strictObject({
    version: z.literal(1),
    ephemeral: ContentPublicKey,
    recipientKeyId: Digest,
    box: CiphertextBox.extend({ ciphertext: ContentBase64Url.length(64) }),
});
export const ContentKeyDirectory = z.strictObject({
    version: z.literal(1),
    accountId: Id,
    revision: Counter,
    previous: Digest.nullable(),
    epoch: Counter,
    keyCommitment: Digest,
    revocationSeq: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    issuedAt: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    recipients: z
        .array(z.union([
        z.strictObject({ id: Id, type: z.enum(['device', 'daemon']), publicKey: ContentPublicKey }),
        z.strictObject({ id: Id, type: z.enum(['prf', 'recovery']), keyId: Id }),
    ]))
        .min(1)
        .max(128),
});
export const SignedContentDirectory = z.strictObject({
    statement: ContentKeyDirectory,
    approval: z.union([
        z.strictObject({
            type: z.literal('passkey'),
            assertion: WebAuthnAssertion.strict(),
            batch: ApprovalBatch.optional(),
        }),
        z.strictObject({
            type: z.literal('device'),
            deviceKeyId: Id,
            signature: ContentBase64Url.max(256),
            /** Additive: a trusted browser's self-join proof of the current ADK (HMAC, key holders only). */
            proof: ContentBase64Url.max(64).optional(),
        }),
    ]),
});
export const ContentDirectoryPublish = z.strictObject({
    directory: SignedContentDirectory,
    wraps: z.array(z.strictObject({ recipientId: Id, wrap: ContentKeyWrap })).max(128),
});
export const ContentDirectoryRead = z.strictObject({
    afterRevision: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    recipientId: Id,
});
export const ContentDirectoryPage = z.strictObject({
    directories: z.array(SignedContentDirectory).max(16),
    head: z.number().int().nonnegative(),
    wrap: ContentKeyWrap.optional(),
    wrapRevision: Counter.optional(),
});
/** The ECDH key is vouched for by the separately enrolled daemon signing identity. */
export const CONTENT_IDENTITY_DOMAIN = 'viko/daemon-content-identity/v1';
export const DaemonContentIdentityProof = z.strictObject({
    statement: z.strictObject({
        version: z.literal(1),
        accountId: Id,
        machineId: Id,
        daemonKeyId: Digest,
        publicKey: ContentPublicKey,
    }),
    signature: ContentBase64Url.length(86),
});
export const ContentIdentityRead = z.strictObject({ machineId: Id });
//# sourceMappingURL=content-directory.js.map